import axios from 'axios';
import { baseUrl } from '../constants/appConstants';
import { setHeaders, setERPType, setSignOutHeaders } from './common';
import { summariseClaimSources, TOKEN_CLAIMS_HEADER } from '../utils/tokenClaims';
import { useSelector } from 'react-redux';

const BASE_URL = baseUrl();

export const uploadFiles = async (data, type, id) => {
  const formData = new FormData();
  for (let i = 0; i < data.length; i++) {
    formData.append('files', data[i]);
  }
  try {
    return await axios.post(
      BASE_URL + `api/v1/file/upload-files-v2?documentType=${type}&documentReference=${id}`,
      formData,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Vendor COI (Certificate of Insurance) compliance from Tier B (which proxies
// the coi-tracker service). Omit `vendor` for the whole company's rows (used to
// build the documents-list lookup); pass a vendor number/name for a single row
// (used by the canvas when the vendor changes). Tier B returns [] when the
// company's vendorCoiCompliance flag is off.
export const callVendorCoiCompliance = async (vendor) => {
  try {
    const query = vendor ? `?vendor=${encodeURIComponent(vendor)}` : '';
    return await axios.get(BASE_URL + `api/v3/company/vendor-coi-compliance${query}`, setHeaders());
  } catch (error) {
    return error;
  }
};

// One document's lien waiver coverage from Tier B (which proxies the
// lien-waiver-tracker service). Tier B answers an `Untracked` row — never an
// error — when the company's vendorLienWaiverCompliance flag is off or the
// tracker is unreachable, so the badge simply stays hidden.
// The same status for a page of documents in one call (the documents list's
// per-row dots). Tier B forwards the ids to the tracker, which grades the book
// once and answers a row per id, in order. Returns [] for no ids.
// Chunked: the Approved tab's "All" page size puts every approved id on one
// request line, which passes nginx's 8 KB header buffer at roughly 300 ids and
// the tracker's 200-id cap before that. One failed chunk degrades alone; the
// rest of the page still gets its markers.
const LIEN_WAIVER_BADGE_CHUNK = 100;
export const callLienWaiverBadges = async (documentIds) => {
  const ids = [
    ...new Set((documentIds || []).map((d) => String(d || '').trim()).filter(Boolean))
  ];
  if (ids.length === 0) return { data: { payload: { data: [] } } };
  const chunks = [];
  for (let i = 0; i < ids.length; i += LIEN_WAIVER_BADGE_CHUNK) {
    chunks.push(ids.slice(i, i + LIEN_WAIVER_BADGE_CHUNK));
  }
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const res = await axios.get(
          BASE_URL +
            `api/v3/company/lien-waiver-badges?documentIds=${encodeURIComponent(chunk.join(','))}`,
          setHeaders()
        );
        const rows = res?.data?.payload?.data;
        return Array.isArray(rows) ? rows : [];
      } catch (error) {
        return [];
      }
    })
  );
  return { data: { payload: { data: results.flat() } } };
};

export const callLienWaiverBadge = async (documentId) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v3/company/lien-waiver-badge?documentId=${encodeURIComponent(documentId)}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getSelectedDocs = async (
  searchKey,
  payload,
  callSource,
  advancedSearch,
  currentPage,
  documentStatus,
  pageSize
) => {
  const newData = encodeURIComponent(searchKey);
  const status = encodeURIComponent(documentStatus);

  try {
    return await axios.post(
      BASE_URL +
        `api/v1/document/v3/get-document?keyword=${newData}&callSource=${callSource}&advancedSearch=${advancedSearch}&offset=${currentPage}&limit=${pageSize}&paginationBasedOnDocumentStatus=${status}`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Advanced document search — independent MongoDB-native endpoint (backend:
// _specs/advanced-document-search.md). Flat, combinable, status-agnostic search. All filters +
// page/limit/sort go in the body; response is { documents: [...], pagination: { total, ... } }.
export const advancedDocumentSearch = async (payload, page, pageSize) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/document/v3/advanced-search`,
      { ...payload, page, limit: pageSize },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getDocumentDetails = async (id, callSource) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/document/get/document/V3/${id}?callSource=${callSource}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getDocumentActivity = async (id) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/document/v3/get-document-activity/${id}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const updateDocument = async (id, body) => {
  try {
    return await axios.put(BASE_URL + `api/v1/document/update/content/${id}`, body, setHeaders());
  } catch (error) {
    return error;
  }
};

// Bulk-resolve the Q&A on the given documents (e.g. from the documents table when
// rows are selected in the In Q&A tab). Sends the selected ids + a resolution comment.
export const resolveQAndA = async (documentIds, comment) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/document/v3/resolve-q-and-a',
      { documentIds, comment },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Bulk-assign one pay control (BE key: accountant) to the selected documents.
export const assignPayControl = async (documentIds, payControl) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/document/v3/assign-pay-control',
      { documentIds, payControl },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Merges the selected Pending/Populated documents into ONE new document (re-run
// through extraction) and deletes the originals once it exists.
export const combineDocuments = async (documentIds, documentType) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/document/v3/combine-documents',
      { documentIds, documentType },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// The documents-list Note on one document. An empty note clears it. Writes
// only the note (status/approval untouched) and logs it to the Activity.
export const updateDocumentNote = async (documentId, note) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/document/v3/update-note',
      { documentId, note },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Custom folders: the selection into one manual folder, or out of theirs when
// folderId is null. Status untouched — see utils/customFolders.js.
export const moveToFolder = async (documentIds, folderId) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/document/v3/move-to-folder',
      { documentIds, folderId: folderId || null },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const sendDocToErp = async (payload) => {
  try {
    return await axios.post(BASE_URL + `api/v1/integration/send-to-erp`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const fetchPoContent = async (poNumber) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v1/integration/purchase-order-content?poNumber=${encodeURIComponent(poNumber)}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getUnusedPoNumbers = async (erpType) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/integration/get-unused-po-numbers?erpType=${encodeURIComponent(erpType)}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const createErpDocument = async (file, content, lineItems, documentType, vendorNumber) => {
  const formData = new FormData();
  formData.append('file', file);
  // content/lineItems must be sent as stringified JSON, not nested objects.
  formData.append('content', JSON.stringify(content));
  formData.append('lineItems', JSON.stringify(lineItems));
  formData.append('documentType', documentType);
  formData.append('vendorNumber', vendorNumber ?? '');
  try {
    // Intentionally do NOT set Content-Type — axios adds the multipart boundary.
    return await axios.post(
      BASE_URL + `api/v1/integration/create-erp-document`,
      formData,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const downloadCSV = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/csv/get-all-documents-csv', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const sendEmailToVendor = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/email/send-email-to-vendor', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const createCsv = async (payload) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/email/send-email-selected-documents',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const saveIntegration = async (integrationData) => {
  try {
    return await axios.post(BASE_URL + `api/v1/oauth/saveIntegration`, integrationData, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const connectIntegration = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/oauth/authUri`, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const createFoundationDirectLinkToken = async (referenceId) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/foundation/link/token/create`,
      { reference_id: referenceId },
      {
        headers: {
          ...setHeaders().headers,
          ...setERPType().headers
        }
      }
    );
  } catch (error) {
    return error;
  }
};

export const diconnectIntegration = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/oauth/disconnect`, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};
export const sendEmailtoAdmin = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/email/send-email-admin', payload);
  } catch (error) {
    return error;
  }
};

export const customerSignup = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/user/sign-up', payload);
  } catch (error) {
    return error;
  }
};

export const createOperator = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/admin-create-operator', payload);
  } catch (error) {
    return error;
  }
};

export const findUser = async (email) => {
  try {
    return await axios.get(BASE_URL + `api/v1/awsCognito/get-user?email=${email}`);
  } catch (error) {
    return error;
  }
};

export const getAllOperators = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/awsCognito/get-all-operators');
  } catch (error) {
    return error;
  }
};

export const signUp = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/sign-up', payload);
  } catch (error) {
    return error;
  }
};

export const deleteOperator = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/admin-delete-operator', payload);
  } catch (error) {
    return error;
  }
};

export const confirmSignup = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/confirm-sign-up', payload);
  } catch (error) {
    return error;
  }
};

export const operatorPassword = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/admin-confirm-password', payload);
  } catch (error) {
    return error;
  }
};
export const resendCode = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/resend-confirmation-code', payload);
  } catch (error) {
    return error;
  }
};

export const signIn = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/sign-in', payload);
  } catch (error) {
    return error;
  }
};

export const forgotPassword = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/forget-password', payload);
  } catch (error) {
    return error;
  }
};

export const updatePassword = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/awsCognito/update-password', payload);
  } catch (error) {
    return error;
  }
};

export const getAllUsers = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/user/get-all-user', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getAllErrors = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/document/get/error/documents', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getErrorDocument = async (id) => {
  try {
    return await axios.get(BASE_URL + `api/v1/document/geterrordocument/${id}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const uploadPdf = async (data) => {
  const files = new FormData();
  for (let i = 0; i < data.length; i++) {
    files.append('files', data[i]);
  }
  try {
    return await axios.post(BASE_URL + 'api/v1/file/upload-split-doc', files, setHeaders());
  } catch (error) {
    return error;
  }
};

export const mergePdf = async (payload) => {
  try {
    return await axios.post(BASE_URL + `api/v1/file/merge-files-v2`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const uploadReconciliationStatement = async (files) => {
  try {
    const form = new FormData();
    (Array.isArray(files) ? files : [files]).forEach((f) => form.append('files', f));
    return await axios.post(
      BASE_URL + 'api/v1/reconciliation-statement/statements/upload',
      form,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const uploadCsv = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/user/update-user-gl-info', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getGlInfo = async (email) => {
  try {
    return await axios.get(BASE_URL + `api/v1/user/get-user-gl-info?email=${email}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getUserInfo = async (name) => {
  try {
    // Report which claims this browser's idToken carried, so Tier B can log the
    // Cognito attribute list and the token claim list together. Tier B never
    // branches on it; an older client that omits it logs as 'not-reported'.
    // The cors middleware sets no allowedHeaders, so it reflects whatever the
    // browser asks for and this needs no CORS change.
    const config = setHeaders();
    return await axios.get(BASE_URL + `api/v1/user/get-user?userName=${name}`, {
      ...config,
      headers: { ...config?.headers, [TOKEN_CLAIMS_HEADER]: summariseClaimSources() }
    });
  } catch (error) {
    return error;
  }
};

export const generateBill = async (payload) => {
  try {
    return await axios.post(BASE_URL + `api/v1/payment/create-payment-link`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getPaymentIntent = async (payload) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/payment/create-payment-intent',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
// Hard resync — clears everything held from the ERP, then re-pulls it. Same body
// as syncData (the backend delegates there once the clear is done). The ordinary
// sync can only add rows, so this is the only path that removes coding options;
// the backend restricts it to the AP role.
export const hardResync = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/integration/hard-resync', payload, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const syncData = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/integration/sync-files', payload, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const downloadQBDFile = async (payload) => {
  try {
    return await axios.post(
      BASE_URL + '/api/v1/integration/qbd/get-qwc-file',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateProfile = async (payload) => {
  try {
    return await axios.patch(BASE_URL + 'api/v1/user/update-user', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

// Persist the user's selected UI theme. Best-effort: failures are swallowed by
// the caller so theme switching always succeeds locally even if the backend
// does not (yet) store the field.
export const updateUserTheme = async (theme) => {
  try {
    return await axios.patch(BASE_URL + 'api/v1/user/update-theme', { theme }, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getCompanyConfiguration = async (companyId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/company/get-configuration?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateCompanyConfiguration = async (payload) => {
  try {
    return await axios.put(BASE_URL + 'api/v3/company/update-configuration', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

// The company-wide document field configuration: four buckets — content, lineItems,
// codes, paymentDetails — each a flat list of { name, enabled, changeable, reason,
// documentTypes, aliases }. Not split per document type; a field carries the types
// it appears on instead.
export const getDocumentFieldConfiguration = async (companyId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/company/get-document-field-configuration?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Partial on both axes: an omitted bucket is untouched, and inside a bucket a field
// you don't name keeps its state — so only what changed needs to go out. At least
// one bucket must be present, and every row must carry `enabled`. Locked rows are
// accepted and returned in `ignoredFields` rather than failing the call.
export const updateDocumentFieldConfiguration = async (payload) => {
  try {
    return await axios.put(
      BASE_URL + 'api/v3/company/update-document-field-configuration',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const changePaymentStatus = async (payload) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/payment/update-payment-status',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const matchPO = async (id, payload) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/integration/match-supporting-doc/${id}`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const checkPoTax = async (payload) => {
  try {
    return await axios.post(BASE_URL + `api/v1/integration/validate-po`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getVendors = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/vendor/get-all-vendor', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getVendorsDocument = async (id) => {
  try {
    return await axios.get(BASE_URL + `api/v3/admin/get-vendor-doc/${id}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const addRole = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/user/add-role', payload, setHeaders());
  } catch (error) {
    return error;
  }
};
export const resendInvite = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/user/resend-invite', payload, setHeaders());
  } catch (error) {
    return error;
  }
};
export const getOwnerUsers = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/user/get-roles', setHeaders());
  } catch (error) {
    return error;
  }
};

export const addPolicy = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v3/company/add-approval-policy', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const deleteUser = async (payload) => {
  try {
    const url = BASE_URL + 'api/v1/user/delete-role';
    const headers = setHeaders();
    return await axios.delete(url, { ...headers, data: payload });
  } catch (error) {
    return error;
  }
};

export const getPolicy = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/company/get-approval-policy', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getInfo = async (name, headers) => {
  try {
    return await axios.get(BASE_URL + `api/v1/user/get-user?userName=${name}`, headers());
  } catch (error) {
    return error;
  }
}; //with seperate headers

// ---- Approval rules v2 (rule-based approvals) -----------------------------
// Separate from the v1 policy endpoints above: a company runs one or the other,
// chosen by the `approvalPolicyV2` company flag. See the backend spec at
// _specs/approvals-policy-v2-spec.md.

export const getApprovalRules = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/approval-policy/rules', setHeaders());
  } catch (error) {
    return error;
  }
};

export const saveApprovalRules = async (payload) => {
  try {
    return await axios.put(BASE_URL + 'api/v3/approval-policy/rules', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

/**
 * Propose a rule set from the ERP's project managers: one rule per manager,
 * that manager's jobs routed to that manager.
 *
 * PROPOSES ONLY — nothing is saved. The reply also names the managers who could
 * not be made approvers, which is what the confirm dialog shows. Saving goes
 * through saveApprovalRules like any other edit.
 */
export const generateApprovalRulesFromErp = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/approval-policy/rules/from-erp', setHeaders());
  } catch (error) {
    return error;
  }
};

// Values for one basis dropdown — job, projectManager, glAccount, vendor, workOrder.
// Every synced job with its project manager, and when jobs last synced — the
// rule builder's coverage line and Jobs tab work out which rule covers each.
export const getApprovalJobs = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/approval-policy/jobs', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getApprovalDimensionValues = async (field) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/approval-policy/dimension-values?field=${field}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Who would approve a document if it were approved right now.
export const getApprovalPreview = async (documentId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/approval-policy/preview?documentId=${documentId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const deletePolicy = async () => {
  try {
    const response = await axios.delete(
      BASE_URL + 'api/v3/company/delete-approval-policy',
      setHeaders()
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteDocument = async (payload) => {
  try {
    const url = BASE_URL + 'api/v1/document/v3/delete-document';
    const headers = setHeaders();
    return await axios.delete(url, { ...headers, data: payload });
  } catch (error) {
    throw error;
  }
};
export const getDashboardRecord = async (start, end, vendorType, callSource) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v1/document/v3/get-all-info-document-dashboard?startDate=${start}&endDate=${end}&vendorType=${vendorType}&callSource=${callSource}
    `,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const addManualUser = async (data, email, amount, name) => {
  const files = new FormData();
  for (let i = 0; i < data.length; i++) {
    files.append('files', data[i]);
  }
  files.append('email', email);
  files.append('amount', amount);
  files.append('name', name);
  try {
    const response = await axios.post(BASE_URL + 'api/v3/admin/add-manual-user', files, {
      ...setHeaders()
    });

    return response;
  } catch (error) {
    return error;
  }
};
export const getManualUsers = async () => {
  try {
    return await axios.get(BASE_URL + `api/v3/admin/get-all-manual-user`, setHeaders());
  } catch (error) {
    return error;
  }
};
export const getManualUserPaymentIntent = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v3/admin/create-payment-intent', payload);
  } catch (error) {
    return error;
  }
};
export const changeManualPaymentStatus = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v3/admin/update-payment-status', payload);
  } catch (error) {
    return error;
  }
};
export const processDuplicateDocument = async (id) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/document/v3/process-duplicate-document?documentId=${id}`,
      '',
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const registerVendor = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/vendor/sign-up', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateVendor = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/vendor/update-doc-limit', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getDocumentCount = async (startDate, endDate, email, role) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v3/admin/get-doc-count?startDate=${startDate}&endDate=${endDate}&email=${email}&role=${role}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const downloadFile = async (payload) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/document/v3/download-documents',
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getApprovers = async (documentId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/document/v3/get-document-approver/${documentId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getDashboardDocumentCount = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/document/v3/get-document-count`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const signOut = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/awsCognito/sign-out`, {
      headers: {
        ...setHeaders().headers,
        ...setSignOutHeaders().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const searchDocumentsbyVendorName = async (vendorName, documentId) => {
  try {
    const newVendor = encodeURIComponent(vendorName);
    return await axios.get(
      BASE_URL +
        `api/v3/attachment/upload-attachment-search?venderName=${newVendor}&docNumber=${documentId}`,
      { ...setHeaders() }
    );
  } catch (error) {
    return error;
  }
};

export const uploadAttachments = async (data, documentIds, instrumentIds, companyId) => {
  const formData = new FormData();
  for (let i = 0; i < data.length; i++) {
    formData.append('files', data[i]);
  }
  const documentParams = documentIds?.map((id) => `documentRef=${id}`)?.join('&');
  const instrumentParams = instrumentIds?.map((id) => `instrumentNo=${id}`)?.join('&');
  const url = `${BASE_URL}api/v3/attachment/upload-attachment-files?${documentParams}&${instrumentParams}&companyId=${companyId}`;
  try {
    return await axios.post(url, formData, setHeaders());
  } catch (error) {
    return error;
  }
};

export const searchAttachmentById = async (id) => {
  try {
    return await axios.get(BASE_URL + `api/v3/attachment/search-attachment?keyword=${id}`, {
      ...setHeaders()
    });
  } catch (error) {
    return error;
  }
};

export const editAttachment = async (payload, id) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v3/attachment/update-attachment/${id}`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const deleteAttachment = async (id, companyId) => {
  try {
    return await axios.delete(
      BASE_URL + `api/v3/attachment/delete-attachment/${id}?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const removeAttachment = async (docId, id, companyId) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v1/document/v3/remove-attachment/${docId}?attachmentId=${id}&companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const searchDeliveryTicketbyVendorName = async (
  vendorName,
  date,
  po,
  invoiceNumber,
  show,
  // The matching signals, as a bag rather than three more positional arguments.
  //
  // They arrived positionally at first and it went wrong immediately: adding
  // `lineDescriptions` in seventh place silently redefined what the Attach
  // modal's seventh argument meant, so it stopped sending `vendorNumber`. The
  // backend applies its strict vendor+job gate only when it has a vendorNumber
  // and otherwise falls back to matching on the vendor NAME alone — so the job
  // quietly stopped gating anything, with no error and nothing in a log.
  //
  // Named keys cannot drift that way: a caller that omits one omits that one.
  //
  //   jobId             the invoice's coded job — a signal, no longer a gate
  //   vendorNumber      the resolved ERP vendor
  //   lineDescriptions  the invoice's line descriptions
  //   documentId        the invoice being reviewed
  //
  // All optional. Sent by a company with delivery-ticket coding on; without
  // them the list is what it always was.
  //
  // `documentId` is what lets the backend rank with the same matcher auto-attach
  // uses. The matcher scores on LINE ITEMS, and the canvas cannot send them:
  // `lineDescriptions` carries descriptions only — no quantity, no UOM — and on
  // a consolidated document it is the single string "Consolidated Items". With
  // the id the backend reads the stored document for its real lines and takes
  // the header from what is on screen.
  //
  // It is also what leaves out the tickets already declined for this invoice —
  // they come back demoted and stop being announced.
  { jobId, vendorNumber, lineDescriptions, documentId } = {}
) => {
  try {
    const newVendor = encodeURIComponent(vendorName);
    const newPo = encodeURIComponent(po);
    const newInvoiceNumber = encodeURIComponent(invoiceNumber);
    // Capped before it goes anywhere near a URL — the backend caps again on the
    // way in, but building a 40KB querystring only to have it rejected by a
    // proxy would fail silently and only on the biggest invoices.
    const descriptions = Array.isArray(lineDescriptions)
      ? lineDescriptions
          .filter((entry) => typeof entry === 'string' && entry.trim() !== '')
          .slice(0, 60)
          .map((entry) => entry.trim().slice(0, 120))
      : [];
    const matching = [
      jobId ? `&jobId=${encodeURIComponent(jobId)}` : '',
      vendorNumber ? `&vendorNumber=${encodeURIComponent(vendorNumber)}` : '',
      descriptions.length
        ? `&lineDescriptions=${encodeURIComponent(JSON.stringify(descriptions))}`
        : '',
      documentId ? `&documentId=${encodeURIComponent(documentId)}` : ''
    ].join('');
    return await axios.get(
      BASE_URL +
        `api/v1/document/v3/search-by-filters?date=${date}&vendor=${newVendor}&purchaseOrder=${newPo}&documentNumber=${newInvoiceNumber}&filter=${show}${matching}`,
      { ...setHeaders() }
    );
  } catch (error) {
    return error;
  }
};

/**
 * "Do not attach this delivery ticket to this invoice automatically."
 *
 * Records the decline; attaches and detaches nothing. The attaching still
 * happens at approval, on the backend, from whatever candidates survive — so
 * this narrows the choice rather than making it.
 */
export const declineDeliveryTicket = async (documentId, ticketId) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/document/v3/decline-delivery-ticket',
      { documentId, ticketId },
      { ...setHeaders() }
    );
  } catch (error) {
    return error;
  }
};

export const attachDeliveryTicket = async (payload) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/attach-documents`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const searchbyChequeNumber = async (key) => {
  try {
    const newKey = encodeURIComponent(key);
    return await axios.get(BASE_URL + `api/v1/integration/search-check_no/${newKey}`, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const getTaskQueue = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/integration/qbd/get-queue`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const clearQueue = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/integration/reset-queue`, {
      headers: {
        ...setHeaders().headers,
        ...setERPType().headers
      }
    });
  } catch (error) {
    return error;
  }
};

export const deleteDeliveryTicket = async (payload) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/remove-attached-documents`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getAttachmentsbyDocId = async (id) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/document/v3/get-attach-documents/${id}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const FetchDefaultCoding = async (number) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/integration/get-default-codes?vendor=${number}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const getSupportingDocument = async (id, payload) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/integration/get-document-by-erp/${id}`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const checkDocNoDuplication = async (id, companyId, docNo, vendor, vendorNumber) => {
  try {
    const newKey = encodeURIComponent(vendor);
    let url =
      BASE_URL +
      `api/v1/document/v3/check-duplicate-document-number/${id}?companyId=${companyId}&documentNumber=${docNo}&vendor=${newKey}`;
    if (vendorNumber !== undefined && vendorNumber !== null && vendorNumber !== '') {
      url += `&vendorNumber=${encodeURIComponent(vendorNumber)}`;
    }
    return await axios.get(url, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getAllBatches = async (companyId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/file/get-all-split-extraction?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getBatchById = async (batchId) => {
  try {
    return await axios.get(BASE_URL + `api/v1/file/split-batch-by-id/${batchId}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateTableColumns = async (payload) => {
  try {
    return await axios.patch(BASE_URL + 'api/v1/user/updateTableContent', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

// Sent as its own request rather than alongside customTableContent on purpose.
// The endpoint validates its body against a strict allow-list, so a backend that
// has not shipped `customTableColumnWidths` yet rejects the whole payload — and
// bundling them would take column *visibility* down with it. Kept separate, the
// two deploy in either order and an unsupported width write just does nothing.
export const updateTableColumnWidths = async (customTableColumnWidths) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/user/updateTableContent',
      { customTableColumnWidths },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
// The user's column order (field names, display order) for the documents grid.
// Its own request for the same reason as the widths above: a backend without
// `customTableColumnOrder` in its allow-list rejects the body, and that must not
// take column visibility or widths down with it.
export const updateTableColumnOrder = async (customTableColumnOrder) => {
  try {
    return await axios.patch(
      BASE_URL + 'api/v1/user/updateTableContent',
      { customTableColumnOrder },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const getSyncedData = async (type, purpose, companyId) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v3/company/get-synched-data?type=${type}&purpose=${purpose}&companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const syncManagersFromErp = async (companyId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/company/get-jobId-manager-based-policy?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getReport = async () => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/document/v3/download-all-documents-report`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getCodeDetails = async (type, recordNumber) => {
  try {
    return await axios.get(
      BASE_URL +
        `api/v1/integration/get-code-details?codeListType=${type}&recordNumber=${recordNumber}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateDocumentType = async (payload) => {
  try {
    return await axios.put(
      BASE_URL + `api/v1/document/v3/changeDocumentTypeFromHilScreen`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getUpdatedApprovers = async (base, value, docId, total) => {
  try {
    // A load-time lookup passes no value at all, which asks the backend where the
    // document stands right now. Omit the param entirely rather than sending an empty
    // string: an empty string is what the canvas sends when a job or vendor is CLEARED,
    // and a preview for a cleared field must not fall back to the coding just removed.
    // Omitting also stops an undefined being encoded into the literal string "undefined".
    const valueParam =
      value === undefined || value === null
        ? ''
        : `value=${encodeURIComponent(value)}&`;
    // The jobTotal base resolves its level from the job match AND the total range,
    // so it sends the total as its own param alongside the job id in `value`.
    const totalParam =
      total === undefined || total === null || total === ''
        ? ''
        : `&total=${encodeURIComponent(total)}`;
    return await axios.get(
      BASE_URL +
        `api/v3/company/get-potential-policy-approver?base=${base}&${valueParam}docId=${docId}${totalParam}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const saveComments = async (id, payload) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/add-comment/${id}`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const pullBackDocument = async (payload) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/reset-approval-flow`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const deleteBatchDoc = async (payload) => {
  try {
    const url = BASE_URL + 'api/v1/file/delete-split-extraction';
    const headers = setHeaders();
    return await axios.delete(url, { ...headers, data: payload });
  } catch (error) {
    return error;
  }
};

export const addComment = async (
  id,
  payload,
  files = [],
  isQuestion,
  isResolved,
  voiceNoteDurations = []
) => {
  try {
    const formData = new FormData();
    const commentArray = Array.isArray(payload) ? payload : [payload];
    formData.append('comment', JSON.stringify(commentArray));
    files.forEach((file) => formData.append('files', file));
    formData.append('isQuestion', isQuestion);
    formData.append('isResolved', isResolved);
    // Positional against `files` — the server reads durations[i] for files[i].
    // Only sent when there is a recording, so every existing caller is byte for
    // byte unchanged.
    if (voiceNoteDurations?.length) {
      formData.append('voiceNoteDurations', JSON.stringify(voiceNoteDurations));
    }
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/add-comment/${id}`,
      formData,
      setHeaders({ 'Content-Type': 'multipart/form-data' })
    );
  } catch (error) {
    return error;
  }
};

/**
 * Edits the text of a comment the caller wrote.
 *
 * `commentId` is the COMMENT's own _id — unlike add-comment above, whose `id`
 * is a documentId. JSON, not multipart: attachments are deliberately not
 * editable, and the backend's Joi schema is a strict object that rejects unknown
 * keys, so the body is exactly `{ comment }` and nothing else.
 *
 * Refusals come back on the HTTP status, and the 403/422 split matters: 403 is
 * "not your comment", 422 is "was yours, but the hour is up".
 */
export const editComment = async (commentId, comment) => {
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/v3/edit-comment/${commentId}`,
      { comment },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

/**
 * Deletes one voice note off a comment. The comment itself survives.
 *
 * Author only and within the same hour edit-comment allows, so the 403/422
 * split means the same thing it does there. The recording is destroyed in S3,
 * so this cannot be undone — callers must confirm before calling.
 */
export const deleteVoiceNote = async (commentId, s3FileKey) => {
  try {
    return await axios.delete(
      BASE_URL + `api/v1/document/v3/comment-attachment/${commentId}`,
      { params: { s3FileKey }, ...setHeaders() }
    );
  } catch (error) {
    return error;
  }
};

export const downloadFileAttachment = async (fileKey, companyId, docId) => {
  try {
    const url = `${BASE_URL}api/v1/file/download-s3-file/${docId}`;

    const params = {
      s3key: fileKey,
      companyId: companyId
    };
    return await axios.get(url, { params, ...setHeaders() });
  } catch (error) {
    return error;
  }
};

export const getJobDivision = async (jobId) => {
  try {
    const job = encodeURIComponent(jobId);
    return await axios.get(
      BASE_URL + `api/v1/integration/get-job-division?jobId=${job}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// "Expense Based Coding": which reviewer group does this GL account route to?
// `glAccount` is the dropdown LABEL ("<accountNumber> - <name>"); the backend
// splits it. Always answers 200 — a GL account with no reviewer group is a
// successful answer, not an error, so read `payload.data.matched`.
export const getGlAccountReviewerGroup = async (glAccount) => {
  try {
    const gl = encodeURIComponent(glAccount);
    return await axios.get(
      BASE_URL + `api/v1/integration/get-gl-reviewer-group?glAccount=${gl}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// "Department Based GL Coding": the department GL for each coding row.
// `rows` are {jobId, costType, phase, workOrder, scope} dropdown LABELS; the
// backend takes the code half. Always 200 — each result carries `matched` and
// its own `reason`, so read `payload.data.results`.
export const resolveDepartmentGlAccounts = async (rows) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/integration/get-department-codes',
      { rows },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getAllExportBatches = async (companyId) => {
  try {
    return await axios.get(
      `${BASE_URL}api/v1/csv/get-all-export-batches?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const downloadExportedCsvBatch = async (batchId, s3Key, companyId) => {
  try {
    return await axios.get(
      `${BASE_URL}api/v1/csv/download-exported-csv-batch/${batchId}?s3key=${encodeURIComponent(
        s3Key
      )}&companyId=${companyId}`,
      {
        ...setHeaders()
      }
    );
  } catch (error) {
    return error;
  }
};

export const updateExportCsvStatus = async (params) => {
  try {
    return await axios.put(`${BASE_URL}api/v1/csv/update/export-csv-status`, params, setHeaders());
  } catch (error) {
    return error;
  }
};

export const regenerateCsvByBatch = async (params) => {
  try {
    return await axios.put(`${BASE_URL}api/v1/csv/regenerate-csv-by-batch`, params, setHeaders());
  } catch (error) {
    return error;
  }
};

export const checkAvailableEmail = async (companyId, email) => {
  try {
    return await axios.get(
      `${BASE_URL}api/v3/company/check-available-email/${companyId}?email=${encodeURIComponent(
        email
      )}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getAllNotifications = async () => {
  try {
    return await axios.get(
      `${BASE_URL}api/v1/notification/get-all-notifications?page=1`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateNotifications = async (notificationIds = []) => {
  try {
    return await axios.put(
      `${BASE_URL}api/v1/notification/update-notification`,
      {
        status: 'read',
        notificationIds
      },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const changeUserRole = async (params) => {
  try {
    return await axios.put(`${BASE_URL}api/v1/awsCognito/change-user-role`, params, setHeaders());
  } catch (error) {
    return error;
  }
};
export const CreateCustomRole = async (companyId, name, permissions) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v3/company/create-custom-role',
      {
        companyId,
        name,
        permissions
      },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
export const deleteCustomRole = async (companyId, name) => {
  try {
    const response = await axios.delete(`${BASE_URL}api/v3/company/delete-custom-role`, {
      data: {
        companyId,
        name
      },
      ...setHeaders()
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

export const updateRoleConfigurations = async (companyId, name, permissions) => {
  try {
    const response = await axios.put(
      `${BASE_URL}api/v3/company/update-role-configurations`,
      {
        companyId,
        name,
        permissions
      },
      setHeaders()
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

export const reProcessDocument = async (id) => {
  try {
    return await axios.get(BASE_URL + `api/v1/document/v3/reprocess-document/${id}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const setCompanyCoiAvailability = async (companyId, coiModuleAvailable) => {
  try {
    return await axios.patch(
      `${BASE_URL}api/v3/admin/company-coi-availability`,
      { companyId, coiModuleAvailable },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateAdminPermissions = async (params) => {
  try {
    return await axios.put(`${BASE_URL}api/v3/admin/update-permissions`, params, setHeaders());
  } catch (error) {
    return error;
  }
};

export const verifyDriveFolder = async (folderId, companyId) => {
  try {
    return await axios.get(`${BASE_URL}api/v3/company/verify-drive-folderId`, {
      params: {
        folderId,
        companyId
      },
      ...setHeaders()
    });
  } catch (error) {
    return error;
  }
};

export const syncDocumentDrive = async (callPurpose, startDate, endDate, companyId) => {
  try {
    const params = new URLSearchParams({
      callPurpose,
      startDate,
      endDate,
      companyId
    });

    return await axios.get(
      `${BASE_URL}api/v1/document/v3/document-drive-sync?${params.toString()}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const assignDocumentToAccountant = async (documentId, accountantEmails) => {
  try {
    return await axios.patch(
      `${BASE_URL}api/v1/document/v3/assign-accountant/${documentId}`,
      { accountantEmail: accountantEmails },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getJobBudgetCoding = async (jobId) => {
  try {
    const job = encodeURIComponent(jobId);
    return await axios.get(
      BASE_URL + `api/v1/integration/get-job-coding?jobId=${job}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getGooglePickerToken = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/oauth/google/picker-token`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getAuthRoute = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/oauth/google/authUri`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const sendAuthCode = async (code) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/oauth/google/callback?code=${encodeURIComponent(code)}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const disconnectGoogleDrive = async () => {
  try {
    const headers = {
      erptype: 'Google',
      ...setHeaders()?.headers
    };

    const response = await axios.get(`${BASE_URL}api/v1/oauth/disconnect`, { headers });
    return response;
  } catch (error) {
    return error;
  }
};

export const getImageBlob = async (key) => {
  try {
    const newKey = encodeURIComponent(key);
    // Tier B now requires the token here and serves only the caller's own
    // company's files (it used to answer anyone).
    const response = await axios.get(BASE_URL + `api/v1/document/image-blob?url=${newKey}`, {
      ...setHeaders(),
      responseType: 'blob'
    });
    return response?.data;
  } catch (error) {
    return error;
  }
};

/**
 * Save the document's annotations along with the client-rendered files that
 * have them burned in. Unlike the rotate endpoint this does not re-run
 * extraction, so the user's coding is left alone.
 */
export const annotateDocument = async (
  id,
  { original, pages, previews, pageIndexes, annotations }
) => {
  const formData = new FormData();
  if (original) formData.append('original', original);
  (pages || []).forEach((file) => formData.append('pages', file));
  (previews || []).forEach((file) => formData.append('previews', file));
  formData.append('pageIndexes', JSON.stringify(pageIndexes || []));
  formData.append('annotations', JSON.stringify(annotations || []));
  try {
    return await axios.patch(BASE_URL + `api/v1/document/annotate/${id}`, formData, setHeaders());
  } catch (error) {
    return error;
  }
};

export const uploadRotatedDocument = async (data, id) => {
  const formData = new FormData();
  for (let i = 0; i < data?.length; i++) {
    formData?.append('files', data[i]);
  }
  try {
    return await axios.patch(
      BASE_URL + `api/v1/document/extract-rotated-document/${id}`,
      formData,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getVistaQueue = async (erpType) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/integration/vista/get-queue?erpType=${erpType}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// ─── Plaid Integration ──────────────────────────────────────────────────────────

export const createPlaidLinkToken = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/plaid/create-link-token', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const exchangePlaidPublicToken = async (publicToken, metadata) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v1/plaid/exchange-public-token',
      { publicToken, metadata },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const disconnectPlaid = async () => {
  try {
    return await axios.post(BASE_URL + 'api/v1/plaid/remove-connection', {}, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getSuggestedEmails = async () => {
  try {
    return await axios.get(BASE_URL + `api/v1/user/get-suggested-emails`, setHeaders());
  } catch (error) {
    return error;
  }
};

// ─── Job Budget Management ─────────────────────────────────────────────────────

export const createJobBudget = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/job-budget/create', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getJobBudgetsByCompany = async (companyId) => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/job-budget/company?companyId=${companyId}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getJobBudgetByJobId = async (companyId, jobId) => {
  try {
    const job = encodeURIComponent(jobId);
    return await axios.get(
      BASE_URL + `api/v1/job-budget/job?companyId=${companyId}&jobId=${job}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getJobBudgetById = async (budgetId) => {
  try {
    return await axios.get(BASE_URL + `api/v1/job-budget/${budgetId}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateJobBudget = async (budgetId, payload) => {
  try {
    return await axios.put(BASE_URL + `api/v1/job-budget/${budgetId}`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const deleteJobBudget = async (budgetId) => {
  try {
    return await axios.delete(BASE_URL + `api/v1/job-budget/${budgetId}`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const addBudgetChangeOrder = async (budgetId, payload) => {
  try {
    return await axios.post(
      BASE_URL + `api/v1/job-budget/${budgetId}/change-order`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const approveBudgetChangeOrder = async (budgetId, changeOrderIndex, payload) => {
  try {
    return await axios.put(
      BASE_URL + `api/v1/job-budget/${budgetId}/change-order/${changeOrderIndex}/approve`,
      payload,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const updateBudgetCommittedCost = async (payload) => {
  try {
    return await axios.put(BASE_URL + 'api/v1/job-budget/cost/committed', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateBudgetActualCost = async (payload) => {
  try {
    return await axios.put(BASE_URL + 'api/v1/job-budget/cost/actual', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getJobBudgetSummary = async (companyId, jobId) => {
  try {
    const job = encodeURIComponent(jobId);
    return await axios.get(
      BASE_URL + `api/v1/job-budget/summary?companyId=${companyId}&jobId=${job}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const importJobBudget = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/job-budget/import', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getJobBudgetQbdSyncStatus = async (companyId, jobId) => {
  try {
    const params = new URLSearchParams({ companyId });
    if (jobId) {
      params.append('jobId', jobId);
    }
    return await axios.get(
      BASE_URL + `api/v1/job-budget/sync-status?${params.toString()}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getAllAdminDocuments = async (page, limit) => {
  try {
    return await axios.get(
      BASE_URL + `api/v3/admin/get-all-documents?page=${page}&limit=${limit}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// `search` is optional and applied server-side before paging, so the
// pagination total is the number of matching companies. Omitted when blank so
// the unfiltered request is exactly what it was before search existed.
export const getAllAdminCompanies = async (page, limit, search = '') => {
  try {
    const term = typeof search === 'string' ? search.trim() : '';
    const searchParam = term ? `&search=${encodeURIComponent(term)}` : '';
    return await axios.get(
      BASE_URL + `api/v3/admin/get-all-companies?page=${page}&limit=${limit}${searchParam}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Who turned Policy, an Integration, or Google Drive on or off for one
// company, newest first. `control` narrows to one of the four; omitted,
// every control comes back. Platform-Admin only, same as get-all-companies.
export const getCompanyAuditLog = async (companyId, page, limit, control = '') => {
  try {
    const controlParam = control ? `&control=${encodeURIComponent(control)}` : '';
    return await axios.get(
      BASE_URL +
        `api/v3/admin/get-company-audit-log?companyId=${companyId}&page=${page}&limit=${limit}${controlParam}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// Foundation sync diagnostics (admin only). `getFoundationCompanies` feeds the
// company picker; `getFoundationDiagnostics` runs the read-only diagnosis
// against that customer's Foundation SQL Server. Pass `dataType` (a glInfo key
// such as `purchaseOrder`) to diagnose one list instead of all of them.
export const getFoundationCompanies = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/admin/foundation-companies', setHeaders());
  } catch (error) {
    return error;
  }
};

// Positional `dryRun`/`values` rather than an options object: PR #1955 calls this
// with dryRun positionally, and an options object would read `true` as having no
// dryRun key — silently turning the dry run off instead of failing loudly.
export const getFoundationDiagnostics = async (companyId, dataType, dryRun, values) => {
  try {
    const params = new URLSearchParams({ companyId });
    if (dataType) params.set('dataType', dataType);
    if (dryRun) params.set('dryRun', 'true');
    // Distinct values of the ERP's status-like columns. Opt-in: it costs a
    // GROUP BY per column, so it is not worth paying for on every page load.
    if (values) params.set('values', 'true');
    return await axios.get(
      BASE_URL + `api/v3/admin/foundation-diagnostics?${params.toString()}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const deleteAdminUser = async (payload) => {
  try {
    return await axios.delete(BASE_URL + 'api/v3/admin/admin-delete-user', {
      data: payload,
      ...setHeaders()
    });
  } catch (error) {
    return error;
  }
};

export const getDocumentIdByStatus = async (status, filtersBy, filter, sort, keyword = '') => {
  try {
    const body = { ...(filtersBy || {}), filter: !!filter, sort: sort || '' };
    delete body.search;
    delete body.currentOwner;
    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    return await axios.post(
      BASE_URL +
        `api/v1/document/v3/get-document-ids-by-status?status=${encodeURIComponent(
          status
        )}${keywordParam}`,
      body,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const createSupport = async (payload) => {
  try {
    return await axios.post(BASE_URL + 'api/v3/admin/create-support', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

// ─── Deltek ComputerEase connector ──────────────────────────────────────────────
// The connector is a Windows program on the customer's machine; these calls
// manage it from the Integration page (config file, status, settings, queue).

export const issueDeltekConnectorConfig = async (payload = {}) => {
  try {
    return await axios.post(BASE_URL + 'api/v1/deltek-connector/config', payload, setHeaders());
  } catch (error) {
    return error;
  }
};

// flavor: 'connector', or 'test' (non-prod: test connector with built-in fake data)
export const getDeltekConnectorDownloadUrl = async (flavor = 'connector') => {
  try {
    return await axios.get(
      BASE_URL + `api/v1/deltek-connector/download-url?flavor=${encodeURIComponent(flavor)}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const getDeltekConnectorStatus = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/deltek-connector/status', setHeaders());
  } catch (error) {
    return error;
  }
};

export const getDeltekQueue = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v1/deltek-connector/queue', setHeaders());
  } catch (error) {
    return error;
  }
};

// ---- File check: is each document's file in Beiing Human, in Google Drive and
// on the customer's computer? (Tier B /api/v1/file-sync, fileSyncReconciliationUtility)

export const getFileSyncSummary = async () => {
  try {
    return await axios.get(`${BASE_URL}api/v1/file-sync/summary`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getFileSyncDocuments = async ({ status, page = 1, limit = 50 } = {}) => {
  try {
    return await axios.get(`${BASE_URL}api/v1/file-sync/documents`, {
      params: { ...(status ? { status } : {}), page, limit },
      ...setHeaders()
    });
  } catch (error) {
    return error;
  }
};

export const runFileSyncReconcile = async (body = {}) => {
  try {
    return await axios.post(`${BASE_URL}api/v1/file-sync/reconcile`, body, setHeaders());
  } catch (error) {
    return error;
  }
};

// The local folder check runs in this browser (utils/localFolderScan.js): it
// reads the expected file names, looks in the folder the user chose, reports.
export const getFileSyncManifest = async ({ page = 1, limit = 1000 } = {}) => {
  try {
    return await axios.get(`${BASE_URL}api/v1/file-sync/manifest`, {
      params: { page, limit },
      ...setHeaders()
    });
  } catch (error) {
    return error;
  }
};

export const postFileSyncLocalReport = async (body) => {
  try {
    return await axios.post(`${BASE_URL}api/v1/file-sync/local-report`, body, setHeaders());
  } catch (error) {
    return error;
  }
};

// Acumatica connection (the backend's ERP layer, /api/v1/erp/:erp). Every answer is
// the usual {metadata: {status, message}, payload: {data}} envelope. Connect and
// reconnect return {authorizeUrl}: the caller sends this window there, Acumatica's
// sign-in page sends it back to the backend, and the backend redirects to
// /userProfile?erp=acumatica&erpStatus=connected|error. Changing the connection is
// Owner / Support / Admin only — the backend enforces that independently.
const ACUMATICA_ERP_PATH = 'api/v1/erp/acumatica';

export const connectAcumatica = async (payload) => {
  try {
    return await axios.post(BASE_URL + `${ACUMATICA_ERP_PATH}/connect`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const reconnectAcumatica = async () => {
  try {
    return await axios.post(BASE_URL + `${ACUMATICA_ERP_PATH}/reconnect`, {}, setHeaders());
  } catch (error) {
    return error;
  }
};

export const getAcumaticaStatus = async () => {
  try {
    return await axios.get(BASE_URL + `${ACUMATICA_ERP_PATH}/status`, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateAcumaticaSettings = async (payload) => {
  try {
    return await axios.patch(BASE_URL + `${ACUMATICA_ERP_PATH}/settings`, payload, setHeaders());
  } catch (error) {
    return error;
  }
};

export const disconnectAcumatica = async () => {
  try {
    return await axios.delete(BASE_URL + `${ACUMATICA_ERP_PATH}/connection`, setHeaders());
  } catch (error) {
    return error;
  }
};

// Before the approval that makes a document Approved: would Acumatica take it as it is
// on screen? The body is the update-content request's own shape (unsaved edits); the
// backend runs Send to ERP's own checks on it and writes nothing. payload.data is
// {checked, ok, errors: [{ruleId, field, line?, message}], warnings, message} —
// `checked: false` means the question could not be asked (do not block on it).
export const validateAcumaticaDocument = async (documentId, body) => {
  try {
    return await axios.post(
      BASE_URL + `${ACUMATICA_ERP_PATH}/validate/${documentId}`,
      body,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

// ─── Unit of measure conversion rules (company.uomConversionEnabled) ───
export const getUomConversionRules = async () => {
  try {
    return await axios.get(BASE_URL + 'api/v3/company/uom-conversion-rules', setHeaders());
  } catch (error) {
    return error;
  }
};

export const createUomConversionRule = async (rule) => {
  try {
    return await axios.post(BASE_URL + 'api/v3/company/uom-conversion-rules', rule, setHeaders());
  } catch (error) {
    return error;
  }
};

export const updateUomConversionRule = async (id, rule) => {
  try {
    return await axios.put(
      BASE_URL + `api/v3/company/uom-conversion-rules/${id}`,
      rule,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const deleteUomConversionRule = async (id) => {
  try {
    return await axios.delete(
      BASE_URL + `api/v3/company/uom-conversion-rules/${id}`,
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};

export const importUomConversionRules = async (rules, replace) => {
  try {
    return await axios.post(
      BASE_URL + 'api/v3/company/uom-conversion-rules/import',
      { rules, replace },
      setHeaders()
    );
  } catch (error) {
    return error;
  }
};
